import { 
  differenceInDays, 
  differenceInMonths,
  format,
  isBefore,
  isAfter,
  addYears,
  setMonth,
  setDate,
  getYear
} from 'date-fns';

/**
 * Determines student type based on grade level
 */
export const getStudentType = (gradeLevel) => {
  if (!gradeLevel) return 'traditional';
  
  const lowerGrade = gradeLevel.toLowerCase();
  
  if (lowerGrade.includes('community college') || lowerGrade.includes('cc')) {
    return 'transfer';
  } else if (['9th', '10th', '11th'].some(grade => lowerGrade.includes(grade))) {
    return 'early_planner';
  } else {
    return 'traditional'; // 12th grade, gap year, etc.
  }
};

/**
 * Get key application deadlines based on student type and current date
 */
export const getApplicationDeadlines = (studentType, currentDate = new Date()) => {
  const currentYear = getYear(currentDate);
  
  switch (studentType) {
    case 'traditional': {
      // For traditional students (seniors applying to start next fall)
      let edDeadline = new Date(currentYear, 10, 1); // November 1
      let rdDeadline = new Date(currentYear + 1, 0, 1); // January 1
      
      // If we're past January, they're looking at next year's cycle
      if (isAfter(currentDate, rdDeadline)) {
        edDeadline = addYears(edDeadline, 1);
        rdDeadline = addYears(rdDeadline, 1);
      }
      
      return {
        earlyDecision: edDeadline,
        regularDecision: rdDeadline,
        primary: isBefore(currentDate, edDeadline) ? edDeadline : rdDeadline,
        cycle: `${currentYear}-${currentYear + 1}`
      };
    }
    
    case 'transfer': {
      // For CC transfer students - general deadlines (not CA-specific)
      let earlyDeadline = new Date(currentYear + 1, 1, 1); // February 1 (common early deadline)
      let priorityDeadline = new Date(currentYear + 1, 2, 1); // March 1 (most common)
      let standardDeadline = new Date(currentYear + 1, 3, 1); // April 1
      
      // If we're past April, they're looking at next year's cycle
      if (isAfter(currentDate, standardDeadline)) {
        earlyDeadline = addYears(earlyDeadline, 1);
        priorityDeadline = addYears(priorityDeadline, 1);
        standardDeadline = addYears(standardDeadline, 1);
      }
      
      return {
        early: earlyDeadline,
        priority: priorityDeadline,
        standard: standardDeadline,
        primary: isBefore(currentDate, earlyDeadline) ? earlyDeadline : priorityDeadline,
        cycle: `Fall ${currentYear + 1}`
      };
    }
    
    case 'early_planner': {
      // For younger students (9th-11th grade)
      // Calculate when they'll be seniors
      const gradeLevel = currentDate; // This will be refined based on actual grade
      const seniorYear = currentYear + 2; // Assuming 10th grader (Beckett)
      
      return {
        firstApplications: new Date(seniorYear, 10, 1), // November of senior year
        summerPrograms: new Date(currentYear + 1, 0, 15), // Next January for summer programs
        primary: new Date(seniorYear, 10, 1),
        cycle: `${seniorYear}-${seniorYear + 1}`
      };
    }
    
    default:
      return getApplicationDeadlines('traditional', currentDate);
  }
};

/**
 * Calculate timeline messaging based on student type and deadlines
 */
export const calculateTimelineMessage = (gradeLevel, currentDate = new Date()) => {
  const studentType = getStudentType(gradeLevel);
  const deadlines = getApplicationDeadlines(studentType, currentDate);
  
  switch (studentType) {
    case 'traditional': {
      const daysToED = differenceInDays(deadlines.earlyDecision, currentDate);
      const daysToRD = differenceInDays(deadlines.regularDecision, currentDate);
      
      if (daysToED > 0 && daysToED <= 60) {
        return {
          primary: `${daysToED} days until Early Decision deadlines`,
          secondary: `${daysToRD} days until Regular Decision`,
          urgency: 'high'
        };
      } else if (daysToED > 0) {
        const monthsToED = differenceInMonths(deadlines.earlyDecision, currentDate);
        return {
          primary: `${monthsToED} months until Early Decision`,
          secondary: `${monthsToED + 2} months until Regular Decision`,
          urgency: 'medium'
        };
      } else if (daysToRD > 0) {
        return {
          primary: `${daysToRD} days until Regular Decision deadlines`,
          secondary: 'Early Decision window has passed',
          urgency: 'high'
        };
      } else {
        return {
          primary: 'Current application cycle has ended',
          secondary: 'Consider gap year or next cycle planning',
          urgency: 'low'
        };
      }
    }
    
    case 'transfer': {
      const daysToEarly = differenceInDays(deadlines.early, currentDate);
      const daysToPriority = differenceInDays(deadlines.priority, currentDate);
      
      if (daysToEarly > 0) {
        const monthsToEarly = Math.floor(daysToEarly / 30);
        return {
          primary: `${monthsToEarly} months until early transfer deadlines`,
          secondary: `${Math.floor(daysToPriority / 30)} months until priority transfer deadlines`,
          urgency: monthsToEarly <= 2 ? 'high' : 'medium',
          transferNote: 'Most competitive schools have February-March deadlines'
        };
      } else if (daysToPriority > 0) {
        const monthsToPriority = Math.floor(daysToPriority / 30);
        return {
          primary: `${monthsToPriority} months until priority transfer deadlines`,
          secondary: 'Early transfer deadlines have passed',
          urgency: daysToPriority <= 60 ? 'high' : 'medium'
        };
      } else {
        return {
          primary: 'Most transfer deadlines have passed',
          secondary: 'Check for rolling admission schools and spring transfer options',
          urgency: 'low'
        };
      }
    }
    
    case 'early_planner': {
      const yearsUntilSenior = Math.floor(differenceInMonths(deadlines.firstApplications, currentDate) / 12);
      const monthsToSummer = differenceInMonths(deadlines.summerPrograms, currentDate);
      
      return {
        primary: `${yearsUntilSenior} years until your first college applications`,
        secondary: monthsToSummer > 0 
          ? `${monthsToSummer} months until summer program applications open`
          : 'Focus on exploring interests and building your foundation',
        urgency: 'low',
        milestone: 'Build strong foundations now for future success'
      };
    }
    
    default:
      return {
        primary: 'Timeline calculation pending',
        secondary: 'Please ensure grade level is selected',
        urgency: 'low'
      };
  }
};

/**
 * Get specific action items based on timeline
 */
export const getTimelineActions = (gradeLevel, currentDate = new Date()) => {
  const studentType = getStudentType(gradeLevel);
  const timeline = calculateTimelineMessage(gradeLevel, currentDate);
  
  const actions = {
    traditional: {
      high: [
        'Finalize your college list immediately',
        'Request transcripts and recommendations this week',
        'Complete Common App personal statement',
        'Draft supplemental essays for top choices'
      ],
      medium: [
        'Build balanced college list (reach/match/safety)',
        'Schedule campus visits or virtual tours',
        'Prepare for standardized tests if needed',
        'Start personal statement brainstorming'
      ],
      low: [
        'Research gap year opportunities',
        'Consider spring admission options',
        'Explore rolling admission schools',
        'Plan for next application cycle'
      ]
    },
    transfer: {
      high: [
        'Complete remaining prerequisite courses',
        'Request community college transcripts',
        'Finalize transfer personal statement',
        'Submit TAG application if eligible'
      ],
      medium: [
        'Research articulation agreements',
        'Connect with transfer advisors',
        'Build list of target schools',
        'Calculate transferable units'
      ],
      low: [
        'Focus on maintaining strong GPA',
        'Explore additional transfer options',
        'Consider spring transfer cycle',
        'Research financial aid for transfers'
      ]
    },
    early_planner: {
      low: [
        'Explore 2-3 deep academic interests',
        'Take challenging courses in areas of strength',
        'Research summer enrichment programs',
        'Build relationships with teachers and mentors',
        'Start a passion project or independent study'
      ]
    }
  };
  
  return actions[studentType]?.[timeline.urgency] || actions.traditional.medium;
};

/**
 * Format deadline for display
 */
export const formatDeadline = (date) => {
  return format(date, 'MMMM d, yyyy');
};

/**
 * Get all relevant deadlines for a student
 */
export const getAllDeadlines = (gradeLevel) => {
  const studentType = getStudentType(gradeLevel);
  const deadlines = getApplicationDeadlines(studentType);
  
  const formattedDeadlines = {};
  Object.entries(deadlines).forEach(([key, value]) => {
    if (value instanceof Date) {
      formattedDeadlines[key] = {
        date: value,
        formatted: formatDeadline(value),
        daysUntil: differenceInDays(value, new Date())
      };
    }
  });
  
  return formattedDeadlines;
};